import { h } from 'preact'
import { useState, useRef, useCallback, useEffect } from 'preact/hooks'
import PropTypes from 'prop-types'

import classNames from 'lib/classNames'
import ParticipantTile from './ParticipantTile'

export default function DraggablePIP({ participant, isLocal, isSpeaker }) {
  const pipRef = useRef(null)
  const [position, setPosition] = useState({ x: 16, y: 16 })
  const [isDragging, setIsDragging] = useState(false)
  const dragOffset = useRef({ x: 0, y: 0 })

  const PIP_WIDTH = 200
  const PIP_HEIGHT = 150

  const clamp = useCallback((pos) => {
    const maxX = window.innerWidth - PIP_WIDTH - 16
    const maxY = window.innerHeight - PIP_HEIGHT - 100
    return {
      x: Math.max(16, Math.min(pos.x, maxX)),
      y: Math.max(16, Math.min(pos.y, maxY)),
    }
  }, [])

  useEffect(() => {
    setPosition(clamp({
      x: window.innerWidth - PIP_WIDTH - 24,
      y: window.innerHeight - PIP_HEIGHT - 120,
    }))
  }, [clamp])

  const handleMouseDown = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    const rect = pipRef.current.getBoundingClientRect()
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    }
    setIsDragging(true)
  }, [])

  const handleTouchStart = useCallback((e) => {
    e.stopPropagation()
    const touch = e.touches[0]
    const rect = pipRef.current.getBoundingClientRect()
    dragOffset.current = {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    }
    setIsDragging(true)
  }, [])

  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e) => {
      setPosition(clamp({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y,
      }))
    }
    const handleTouchMove = (e) => {
      const touch = e.touches[0]
      setPosition(clamp({
        x: touch.clientX - dragOffset.current.x,
        y: touch.clientY - dragOffset.current.y,
      }))
    }
    const handleEnd = () => setIsDragging(false)

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleEnd)
    window.addEventListener('touchmove', handleTouchMove, { passive: true })
    window.addEventListener('touchend', handleEnd)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleEnd)
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('touchend', handleEnd)
    }
  }, [isDragging, clamp])

  const pipClass = classNames('DraggablePIP', {
    dragging: isDragging,
  })

  return <div
    ref={pipRef}
    className={pipClass}
    style={{
      position: 'absolute',
      left: position.x + 'px',
      top: position.y + 'px',
      width: PIP_WIDTH + 'px',
      height: PIP_HEIGHT + 'px',
      zIndex: 50,
      cursor: isDragging ? 'grabbing' : 'grab',
    }}
    onMouseDown={handleMouseDown}
    onTouchStart={handleTouchStart}
  >
    <ParticipantTile
      participant={participant}
      isLocal={isLocal}
      isSpeaker={isSpeaker}
    />
  </div>
}

DraggablePIP.propTypes = {
  participant: PropTypes.object.isRequired,
  isLocal: PropTypes.bool,
  isSpeaker: PropTypes.bool,
}
